<template lang="pug">
.waiter-progress-histories-tab(v-cloak)
  .mb-3
  .buttons.d-flex.flex-row.justify-content-end
    button.btn.register-btn(@click="gotoWaiterProgressHistoryRegister") 新規作成
  .mb-2
  WaiterProgressHistoriesTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap"
    @onEdit="gotoWaiterProgressHistoryEdit"
    @onDelete="confirmDeleteModal"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="waiter-progress-histories-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・タイトル
          div ・内容
    // フィルタリング(更新者／記載者)
    span.table-option-text 更新者／記載者:
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter.writer"
        :options="writerFilterOptions"
        size="md"
      )
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )

  OkCancelModal(v-model="showDeleteConfirmModal" :title="showDeleteConfirmModalTitle" @ok="deleteWaiterProgressHistory(targetWaiterProgressHistory)")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";

import { userStore, itemListFilterStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";
import LoginUser from "@/store/LoginUser";
import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";
import { errorHandle } from "@/errorHandler";

import WaiterProgressHistoriesService, { WaiterProgressHistoriesGetModel } from "@/commons/services/WaiterProgressHistories";
import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import { AdmissionApplication } from "@/commons/services/Waiters";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import WaiterProgressHistoriesTable, {
  WaiterProgressHistoriesTableField,
  WaiterProgressHistoriesTableItem,
  FilterSetting,
  WriterFilterOptionValue,
  ColumnType,
  UpdateHistory
} from "@/pc/containers/waiters/WaiterProgressHistoriesTable.vue";
import {
  RoomType,
  TemplateItem,
  TemplateItemAddress,
  TemplateItemBirthday,
  TemplateItemCheckbox,
  TemplateItemFile,
  TemplateItemGroup,
  TemplateItemMultipleCheckbox,
  TemplateItemName,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSelect,
  TemplateItemSwitchCheckbox,
  TemplateItemTel,
  TemplateItemText,
  TemplateItemYearMonth,
  TemplateItemYearMonthDate,
  TemplateType
} from "@/commons/services/AdmissionApplicationTemplates";

/**
 * ### 経過記録タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OkCancelModal,
    PageLoading,
    WaiterProgressHistoriesTable
  }
})
export default class WaiterProgressHistoriesTab extends Vue {
  /** 待機者経過履歴サービス */
  @Inject("WaiterProgressHistoriesService") waiterProgressHistoriesService!: WaiterProgressHistoriesService;
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 列名 */
  fields: Array<WaiterProgressHistoriesTableField> = [];
  /** 行情報 */
  items: Array<WaiterProgressHistoriesTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", writer: WriterFilterOptionValue.ALL };
  /** フィルター選択項目 */
  writerFilterOptions: Array<{ value: string; text: string }> = [
    { value: WriterFilterOptionValue.ALL, text: "全て" },
    { value: WriterFilterOptionValue.MINE_FACILITY, text: "自施設" },
    { value: WriterFilterOptionValue.OTHER_FACILITY, text: "他施設" },
    { value: WriterFilterOptionValue.WAITER, text: "申込者" }
  ];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.waiter.waiterProgressHistoriesTable.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.waiter.waiterProgressHistoriesTable.pageOptions;

  /** 削除確認モーダル表示可否 */
  showDeleteConfirmModal: boolean = false;
  /** 削除確認モーダルタイトル */
  showDeleteConfirmModalTitle: string = "";
  /** 状態更新、削除対象 */
  targetWaiterProgressHistory: WaiterProgressHistoriesGetModel | null = null;

  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `waiter-progress-histories-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### 画面情報をリフレッシュする
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<WaiterProgressHistoriesTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.waiter.waiterProgressHistoriesTable.perPage);
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<WaiterProgressHistoriesTableField> {
    let fields: Array<WaiterProgressHistoriesTableField> = [];

    fields.push({ key: "datetime", label: "日時", sortable: false, sortDirection: "desc", class: "datetime text-center" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "text", label: "内容", sortable: false, sortDirection: "desc", class: "text" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    fields.push({ key: "writer", label: "更新者／記載者", sortable: false, sortDirection: "desc", class: "writer" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["waiter-progress-histories-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    const residentIdInSystem: number = Number(this.$route.params.idinsystem);
    this.setRows(residentIdInSystem);

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    // 利用者テーブルに渡す各プロパティを作成する
    const waiterProgressHistories: Array<WaiterProgressHistoriesGetModel> = await this.getWaiterProgressHistories(residentIdInSystem);
    const items: Array<WaiterProgressHistoriesTableItem> = this.createItems(waiterProgressHistories);
    this.items = items;
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### 閲覧可能施設を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設のMAPを作成する
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);
    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }
    return waiterViewableFacilitiesMap;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(residentIdInSystem: number) {
    try {
      const count: number = await this.waiterProgressHistoriesService.getCount(userStore, residentIdInSystem);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 待機者経過履歴を取得する
   *
   * - 取得制限をしないためstartIndex、endIndexを指定しない
   */
  public async getWaiterProgressHistories(residentIdInSystem: number): Promise<Array<WaiterProgressHistoriesGetModel>> {
    const record: Array<WaiterProgressHistoriesGetModel> = await this.waiterProgressHistoriesService.gets(userStore, residentIdInSystem);
    return record;
  }

  /**
   * ### 行情報を作成する
   */
  public createItems(waiterProgressHistories: Array<WaiterProgressHistoriesGetModel>): Array<WaiterProgressHistoriesTableItem> {
    let items: Array<WaiterProgressHistoriesTableItem> = [];

    for (const history of waiterProgressHistories) {
      const columnType: ColumnType = this.getColumnType(history);

      items.push({
        ...history,
        columnType: columnType,
        updateHistories: columnType == ColumnType.HISTORY ? this.createUpdateHistories(history) : [],
        showAllText: false
      });
    }

    return items;
  }

  /**
   * ### カラム種別を取得する
   */
  public getColumnType(history: WaiterProgressHistoriesGetModel): ColumnType {
    if (history.text != null) {
      return ColumnType.TEXT;
    } else if (history.beforeStatus != null) {
      return ColumnType.STATUS;
    } else if (history.beforeValue != null) {
      return ColumnType.HISTORY;
    } else {
      return ColumnType.UNKNOWN;
    }
  }

  /**
   * ### 変更前値・変更後値のペアの経過記録を作成する
   */
  public createUpdateHistories(history: WaiterProgressHistoriesGetModel): Array<UpdateHistory> {
    let histories: Array<UpdateHistory> = [];

    const beforeValue: AdmissionApplication = history.beforeValue as AdmissionApplication;
    let beforeItemIdMap: Map<string, TemplateItem> = new Map<string, TemplateItem>();
    this.createItemIdMap(beforeValue.templateAnswer.properties, beforeItemIdMap);

    const afterValue: AdmissionApplication = history.afterValue as AdmissionApplication;
    let afterItemIdMap: Map<string, TemplateItem> = new Map<string, TemplateItem>();
    this.createItemIdMap(afterValue.templateAnswer.properties, afterItemIdMap);

    histories = this.createHistories(beforeItemIdMap, afterItemIdMap);

    return histories;
  }

  /**
   * ### テンプレート要素のMAPを作成する
   *
   * - key：要素ID、value：要素自体
   */
  public createItemIdMap(properties: Array<TemplateItem>, itemMap: Map<string, TemplateItem>): void {
    for (const property of properties) {
      // 配下にperopetiesがあれば再帰検索
      if (this.hasProperties(property) == true) {
        // sectionは比較項目にはならないので再帰呼び出し
        if (property.type == TemplateType.SECTION) {
          const section: TemplateItemSection = property as TemplateItemSection;

          if (section.properties && Array.isArray(section.properties)) {
            this.createItemIdMap(section.properties, itemMap);
          }
        }
        // TODO groupの比較は将来的にはグループ単位で表現したいが、現状はその中の項目単位で比較させるため再帰呼び出し
        else if (property.type == TemplateType.GROUP) {
          const group: TemplateItemGroup = property as TemplateItemGroup;

          if (group.properties && Array.isArray(group.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of group.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.createItemIdMap(childProperties, itemMap);
              }
            }
          }
        }
        // TODO radioGroupの比較は将来的にはグループ単位で表現したいが、現状はその中の項目単位で比較させるため再帰呼び出し
        else if (property.type == TemplateType.RADIO_GROUP) {
          const radioGroup: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (radioGroup.properties && Array.isArray(radioGroup.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of radioGroup.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.createItemIdMap(childProperties.properties, itemMap);
              }
            }
          }
        }
        // TODO switchCheckboxの比較は将来的にはグループ単位で表現したいが、現状はその中の項目単位で比較させるため再帰呼び出し
        else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const switchCheckbox: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (switchCheckbox.properties && Array.isArray(switchCheckbox.properties)) {
            this.createItemIdMap(switchCheckbox.properties, itemMap);
          }
        }
      }

      itemMap.set(property.id, property);
    }
  }

  /**
   * ### propertiesを持っているかを取得する
   */
  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 履歴情報を作成する
   */
  public createHistories(beforeItemIdMap: Map<string, TemplateItem>, afterItemIdMap: Map<string, TemplateItem>): Array<UpdateHistory> {
    let updateHistories: Array<UpdateHistory> = [];

    beforeItemIdMap.forEach((beforeItem: TemplateItem, key: string) => {
      const beforeValue: { class: string | string[]; value: any } = this.getItemValue(beforeItem);
      const afterItem: TemplateItem | undefined = afterItemIdMap.get(key);

      // 削除されたことを履歴に残す
      // TODO answerが削除されることがありえるのか？
      if (afterItem == undefined) {
        updateHistories.push({
          type: beforeItem.type,
          columnName: beforeItem.label,
          beforeClass: beforeValue.class,
          beforeValue: beforeValue.value,
          afterClass: "",
          afterValue: "削除"
        });
      }
      // 比較して差分があれば履歴に残す
      else if (this.isItemValueEquals(beforeItem, afterItem, beforeItem.type) == false) {
        const afterValue: { class: string | string[]; value: any } = this.getItemValue(afterItem);

        updateHistories.push({
          type: beforeItem.type,
          columnName: beforeItem.label,
          beforeClass: beforeValue.class,
          beforeValue: beforeValue.value,
          afterClass: afterValue.class,
          afterValue: afterValue.value
        });
      }
    });

    afterItemIdMap.forEach((afterItem: TemplateItem, key: string) => {
      const afterValue: { class: string | string[]; value: any } = this.getItemValue(afterItem);
      const beforeItem: TemplateItem | undefined = beforeItemIdMap.get(key);

      // 追加されたことを履歴に残す
      // TODO answerが削除されることがありえるのか？
      if (beforeItem == undefined) {
        updateHistories.push({
          type: afterItem.type,
          columnName: afterItem.label,
          beforeClass: "",
          beforeValue: "未登録",
          afterClass: afterValue.class,
          afterValue: afterValue.value
        });
      }
    });

    return updateHistories;
  }

  /**
   * ### 要素の値を取得する
   */
  public getItemValue(item: TemplateItem): { class: string | string[]; value: any } {
    switch (item.type) {
      case TemplateType.TEXT:
      case TemplateType.TEXTAREA: {
        const textItem: TemplateItemText = item as TemplateItemText;

        return {
          class: textItem.answer ? "" : "no-input",
          value: textItem.answer ? textItem.answer : "未入力"
        };
      }
      case TemplateType.RADIO: {
        const radioItem: TemplateItemRadio = item as TemplateItemRadio;
        for (const option of radioItem.options) {
          if (radioItem.answer == option.value) {
            return {
              class: "",
              value: option.text
            };
          }
        }

        return {
          class: "no-input",
          value: "未入力"
        };
      }
      case TemplateType.SELECT: {
        const selectItem: TemplateItemSelect = item as TemplateItemSelect;
        let answers: string[] = [];
        for (const option of selectItem.options) {
          for (const answer of selectItem.answer) {
            if (answer == option.value) {
              answers.push(option.text);
            }
          }
        }

        return {
          class: 0 < answers.length ? "" : "no-input",
          value: 0 < answers.length ? answers.join("、") : "未入力"
        };
      }
      case TemplateType.CHECKBOX: {
        const checkboxItem: TemplateItemCheckbox = item as TemplateItemCheckbox;

        return {
          class: "",
          value: checkboxItem.answer == true ? "はい" : "いいえ"
        };
      }
      case TemplateType.MULTIPLE_CHECKBOX: {
        const multipleCheckboxItem: TemplateItemMultipleCheckbox = item as TemplateItemMultipleCheckbox;

        const output: string[] = [];
        for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
          if (multipleCheckboxItem.answer[i] == true) {
            output.push(multipleCheckboxItem.questions[i]);
          }
        }
        return {
          class: 0 < output.length ? "" : "no-input",
          value: 0 < output.length ? output.join(";") : "未入力"
        };
      }
      case TemplateType.ADDRESS: {
        const addressItem: TemplateItemAddress = item as TemplateItemAddress;

        const postalCodeClass: string = addressItem.answer && addressItem.answer.postalCode ? "" : "no-input";
        const postalCode: string = addressItem.answer && addressItem.answer.postalCode ? addressItem.answer.postalCode : "未入力";

        const addressClass: string = addressItem.answer && addressItem.answer.address ? "" : "no-input";
        const address: string = addressItem.answer && addressItem.answer.address ? addressItem.answer.address : "未入力";

        return {
          class: [postalCodeClass, addressClass],
          value: [`郵便番号：${postalCode}`, `住所：${address}`]
        };
      }
      case TemplateType.BIRTHDAY: {
        const birthdayItem: TemplateItemBirthday = item as TemplateItemBirthday;

        const yearClass: string = birthdayItem.answer && birthdayItem.answer.year ? "" : "no-input";
        const year: string = birthdayItem.answer && birthdayItem.answer.year ? birthdayItem.answer.year : "未入力";

        const monthClass: string = birthdayItem.answer && birthdayItem.answer.month ? "" : "no-input";
        const month: string = birthdayItem.answer && birthdayItem.answer.month ? birthdayItem.answer.month : "未入力";

        const dateClass: string = birthdayItem.answer && birthdayItem.answer.date ? "" : "no-input";
        const date: string = birthdayItem.answer && birthdayItem.answer.date ? birthdayItem.answer.date : "未入力";

        return {
          class: [yearClass, monthClass, dateClass],
          value: [`${year}年`, `${month}月`, `${date}日`]
        };
      }
      case TemplateType.YEAR_MONTH_DATE: {
        const yearMonthDateItem: TemplateItemYearMonthDate = item as TemplateItemYearMonthDate;

        const yearClass: string = yearMonthDateItem.answer && yearMonthDateItem.answer.year ? "" : "no-input";
        const year: string = yearMonthDateItem.answer && yearMonthDateItem.answer.year ? yearMonthDateItem.answer.year : "未入力";

        const monthClass: string = yearMonthDateItem.answer && yearMonthDateItem.answer.month ? "" : "no-input";
        const month: string = yearMonthDateItem.answer && yearMonthDateItem.answer.month ? yearMonthDateItem.answer.month : "未入力";

        const dateClass: string = yearMonthDateItem.answer && yearMonthDateItem.answer.date ? "" : "no-input";
        const date: string = yearMonthDateItem.answer && yearMonthDateItem.answer.date ? yearMonthDateItem.answer.date : "未入力";

        return {
          class: [yearClass, monthClass, dateClass],
          value: [`${year}年`, `${month}月`, `${date}日`]
        };
      }
      case TemplateType.YEAR_MONTH: {
        const yearMonthItem: TemplateItemYearMonth = item as TemplateItemYearMonth;

        const yearClass: string = yearMonthItem.answer && yearMonthItem.answer.year ? "" : "no-input";
        const year: string = yearMonthItem.answer && yearMonthItem.answer.year ? yearMonthItem.answer.year : "未入力";

        const monthClass: string = yearMonthItem.answer && yearMonthItem.answer.month ? "" : "no-input";
        const month: string = yearMonthItem.answer && yearMonthItem.answer.month ? yearMonthItem.answer.month : "未入力";

        return {
          class: [yearClass, monthClass],
          value: [`${year}年`, `${month}月`]
        };
      }
      case TemplateType.ROOM_TYPE: {
        // const roomTypeItem: TemplateItemRoomType = item as TemplateItemRoomType;
        // let resutnValues: string[] = [];
        // for (const roomType of roomTypeItem.answer) {
        // }
        return {
          class: "",
          value: "部屋種類が更新されました"
        };
      }
      case TemplateType.FILE: {
        const fileItem: TemplateItemFile = item as TemplateItemFile;

        // botの仕様でnullが格納されてしまう場合があるので、nullの場合は空配列を格納する
        if (fileItem.answer == null) {
          fileItem.answer = [];
        }

        const nameClass: string = fileItem.answer.length != 0 ? "" : "no-input";
        const fileNames: string[] = [];
        for (const file of fileItem.answer) {
          fileNames.push(file!.name);
        }
        const name: string = fileItem.answer.length != 0 ? fileNames.join("、") : "未入力";

        return {
          class: nameClass,
          value: name
        };
      }
      case TemplateType.TEL: {
        const telItem: TemplateItemTel = item as TemplateItemTel;
        const existAnswer: boolean = !telItem.answer ? false : telItem.answer.first || telItem.answer.second || telItem.answer.third ? true : false;

        return {
          class: existAnswer ? "" : "no-input",
          value: existAnswer ? `${telItem.answer.first} － ${telItem.answer.second} － ${telItem.answer.third}` : "未入力"
        };
      }
      case TemplateType.NAME: {
        const nameItem: TemplateItemName = item as TemplateItemName;
        const existAnswer: boolean = !nameItem.answer
          ? false
          : nameItem.answer.firstName || nameItem.answer.secondName || nameItem.answer.firstKanaName || nameItem.answer.secondKanaName
          ? true
          : false;

        return {
          class: existAnswer ? "" : "no-input",
          value: existAnswer ? `${nameItem.answer.secondName} ${nameItem.answer.firstName}（${nameItem.answer.secondKanaName} ${nameItem.answer.firstKanaName}）` : "未入力"
        };
      }
      default: {
        return {
          class: "",
          value: ""
        };
      }
    }
  }

  /**
   * ### テンプレート要素を比較して同じであるかを返す
   */
  public isItemValueEquals(beforeValue: TemplateItem, afterValue: TemplateItem, itemType: TemplateType): boolean {
    switch (itemType) {
      case TemplateType.TEXT:
      case TemplateType.TEXTAREA:
      case TemplateType.RADIO:
      case TemplateType.SELECT:
      case TemplateType.ADDRESS:
      case TemplateType.BIRTHDAY:
      case TemplateType.YEAR_MONTH_DATE:
      case TemplateType.YEAR_MONTH:
      case TemplateType.TEL:
      case TemplateType.NAME: {
        const beforeItem: { answer: any } = beforeValue as any;
        const afterItem: { answer: any } = afterValue as any;
        // nullの場合に文字列の「null」になるのでここで空文字に変換しておく
        const beforeAnswer: string = beforeItem.answer ? JSON.stringify(beforeItem.answer) : "";
        const afterAnswer: string = afterItem.answer ? JSON.stringify(afterItem.answer) : "";

        return beforeAnswer == afterAnswer ? true : false;
      }
      case TemplateType.CHECKBOX:
      case TemplateType.SWITCH_CHECKBOX: {
        // 初期値がNULLの場合があるので、before：nullでかつafter：falseなら変更していない扱いにする
        const beforeItem: TemplateItemCheckbox = beforeValue as TemplateItemCheckbox;
        const afterItem: TemplateItemCheckbox = afterValue as TemplateItemCheckbox;
        const beforeAnswer: boolean = beforeItem.answer ? beforeItem.answer : false;
        const afterAnswer: boolean = afterItem.answer ? afterItem.answer : false;
        return beforeAnswer == afterAnswer ? true : false;
      }
      case TemplateType.MULTIPLE_CHECKBOX: {
        // 初期値がNULLの場合があるので、answerのnullはfalseに置き換える
        const beforeItem: TemplateItemMultipleCheckbox = beforeValue as TemplateItemMultipleCheckbox;
        const beforeAnswers = [];
        for (const beforeAnswer of beforeItem.answer) {
          beforeAnswers.push(beforeAnswer == true ? true : false);
        }
        const afterItem: TemplateItemMultipleCheckbox = afterValue as TemplateItemMultipleCheckbox;
        const afterAnswers = [];
        for (const afterAnswer of afterItem.answer) {
          afterAnswers.push(afterAnswer == true ? true : false);
        }

        const beforeAnswer: string = JSON.stringify(beforeAnswers);
        const afterAnswer: string = JSON.stringify(afterAnswers);
        return beforeAnswer == afterAnswer ? true : false;
      }
      case TemplateType.FILE: {
        // TODO ファイル名以外の比較方法が現在無し
        // ファイルURLで比較する場合、ファイルに変更が無ければアップロードしなおさない対応が必要なので要検討
        const beforeFileItem: TemplateItemFile = beforeValue as TemplateItemFile;
        const afterFileItem: TemplateItemFile = afterValue as TemplateItemFile;

        // botの仕様で、空配列ではなくnullが格納されてしまう問題があるので、置き換えをする
        if (beforeFileItem.answer == null) {
          beforeFileItem.answer = [];
        }
        if (afterFileItem.answer == null) {
          afterFileItem.answer = [];
        }
        // ファイル数が変わっていれば変更している扱い
        if (beforeFileItem.answer.length != afterFileItem.answer.length) {
          return false;
        }

        const beforeFileNames: string[] = [];
        const afterFileNames: string[] = [];
        for (let i = 0; i < beforeFileItem.answer.length; i++) {
          beforeFileNames.push(beforeFileItem.answer[i]!.name);
          afterFileNames.push(afterFileItem.answer[i]!.name);
        }
        const beforeAnswer: string = JSON.stringify(beforeFileNames);
        const afterAnswer: string = JSON.stringify(afterFileNames);
        return beforeAnswer == afterAnswer ? true : false;
      }
      case TemplateType.ROOM_TYPE: {
        const beforeItem: TemplateItemRoomType = beforeValue as TemplateItemRoomType;
        const afterItem: TemplateItemRoomType = afterValue as TemplateItemRoomType;
        // answerの配列順は入れ替わることがあるので、facilityId昇順にソートしてから比較する
        const sortedBeforeAnswer: Array<RoomType> = beforeItem.answer.concat().sort((a, b) => a.facilityId - b.facilityId);
        const sortedAfterAnswer: Array<RoomType> = afterItem.answer.concat().sort((a, b) => a.facilityId - b.facilityId);
        const beforeAnswer: string = JSON.stringify(sortedBeforeAnswer);
        const afterAnswer: string = JSON.stringify(sortedAfterAnswer);
        return beforeAnswer == afterAnswer ? true : false;
      }
      default: {
        return true;
      }
    }
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 経過記録登録画面へ遷移する
   */
  public gotoWaiterProgressHistoryRegister(): void {
    tabIndexesStore.setWaiterDetailTabIndex({ index: 2 });
    this.$router.push({
      name: PcUrl.WaiterProgressHistoryRegister,
      params: { facilityid: this.$route.params.facilityid, residentid: this.$route.params.residentid, idinsystem: this.$route.params.idinsystem }
    });
    return;
  }

  /**
   * ### 経過記録編集画面へ遷移する
   */
  public gotoWaiterProgressHistoryEdit(item: WaiterProgressHistoriesTableItem): void {
    tabIndexesStore.setWaiterDetailTabIndex({ index: 2 });
    this.$router.push({
      name: PcUrl.WaiterProgressHistoryEdit,
      params: { facilityid: this.$route.params.facilityid, residentid: this.$route.params.residentid, idinsystem: this.$route.params.idinsystem, historyid: `${item.id}` }
    });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public confirmDeleteModal(item: WaiterProgressHistoriesTableItem): void {
    this.showDeleteConfirmModalTitle = `タイトル：${item.title} の経過記録を削除しますか？`;
    this.targetWaiterProgressHistory = item;

    this.showDeleteConfirmModal = true;

    return;
  }

  /**
   * ### 経過記録の削除を親コンポーネントに通知する
   */
  public deleteWaiterProgressHistory(history: WaiterProgressHistoriesGetModel): void {
    this.$emit("onDelete", history);
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
.waiter-progress-histories-tab {
  position: relative;

  .buttons {
    .register-btn {
      width: 120px;
    }
  }
}
</style>
